/**
 * **The Rozliczenia tab's reading of the ledger** (T-C2) — the clients' balances furthest behind
 * first, one client's history as rows, and the sessions whose charge may still be cancelled.
 *
 * ── A standing is said, never signed ───────────────────────────────────────────────────────────
 * Below zero a balance is SESSIONS TO SETTLE, never a debt and never a minus sign (R16), and the
 * count is the server's `sessionsOwed` — `sessionCredit`'s `creditStanding` is the one reading of
 * the two figures, and every phrase here goes through it. (R11's text still speaks of "a signed
 * number of credits"; R16, the mockups and the mobile app all say sessions to settle, and so does
 * this.)
 *
 * ── Why the app sorts at all ───────────────────────────────────────────────────────────────────
 * `GET /credit-ledger/summary` publishes ids and figures and no names, so the list is married to
 * the roster. The server's order is kept — sessions owed descending, then the thinnest balance —
 * and ties fall to the NAME rather than to the id, so two clients standing in the same place do not
 * swap rows when a third joins. The settlement screen applies no warning threshold (R11's "from
 * two onwards" is for surfaces that create sessions): it exists to state what is outstanding.
 *
 * ── The history's rows ─────────────────────────────────────────────────────────────────────────
 * A movement's kind is a string that only grows (R20): an unknown one keeps its place, its amount
 * and its note under a neutral title rather than being hidden. A deduction — a session that cost a
 * credit, projected by the server at the session's own instant — is named by the session when the
 * client's schedule in hand holds it ("Trening · pt 18.09, 10:00", "Nieobecność · …"); without it,
 * by its moment alone. A forgiveness moves no amount and still moves the balance by one, so it
 * names the session it took back.
 *
 * Pure: no React, no DOM, no query hook, no i18next — `t` arrives as a parameter.
 */

import type { TFunction } from 'i18next';

import type {
  TrainWithMeModulesBookingDtosResponsesCreditLedgerMovementResponse as LedgerMovement,
  TrainWithMeModulesBookingDtosResponsesCreditLedgerSummaryResponse as CreditSummary,
} from '~/shared/api/generated/model';

import {
  creditStanding,
  standingOfBalance,
  standingShort,
  type CreditStanding,
} from '~/features/credit-ledger';

import { sessionTitle, type BookingSession } from './bookingSession';
import { chargeMayBeForgiven, isChargeForgiven } from './sessionActions';

import { listDay } from './scheduleWords';
import { shiftDay, wallClock, wallReading, type CivilDay } from './trainerClock';

const LEDGER = 'web:booking.ledger';

/** U+2212: the history's figures are tabular, and a hyphen is neither their width nor height. */
const MINUS = '−';

// ── The balances list ────────────────────────────────────────────────────────────────────────

export interface ClientBalanceRow {
  readonly customerId: number;
  /** `null` while the roster has no name for this id — the row still stands. */
  readonly name: string | null;
  readonly standing: CreditStanding;
}

export function clientBalanceRows(
  summary: CreditSummary,
  names: ReadonlyMap<number, string>,
  locale: string,
): ClientBalanceRow[] {
  if (!summary.creditsEnabled) return [];
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  return (summary.clients ?? [])
    .map((row) => ({
      customerId: row.customerId,
      name: names.get(row.customerId) ?? null,
      standing: creditStanding(row),
      owed: Math.max(0, row.sessionsOwed),
      balance: row.balance,
    }))
    .sort(
      (a, b) =>
        b.owed - a.owed ||
        a.balance - b.balance ||
        collator.compare(a.name ?? '', b.name ?? '') ||
        a.customerId - b.customerId,
    )
    .map(({ customerId, name, standing }) => ({ customerId, name, standing }));
}

// ── The history ──────────────────────────────────────────────────────────────────────────────

const KNOWN_KINDS: ReadonlySet<string> = new Set([
  'grant',
  'settlement',
  'adjustment',
  'forgiveness',
  'deduction',
]);

/** The two charges R14 is about, in the calendar's own words for them. */
const DISPUTED: Readonly<Record<string, string>> = {
  LateCancelled: 'calendar.status.lateCancelled',
  NoShow: 'calendar.status.noShow',
};

export interface LedgerRow {
  readonly key: string;
  /** „wt 22.09" on the trainer's clock; empty when the moment is unreadable. */
  readonly day: string;
  readonly title: string;
  readonly meta: string | null;
  /** „+4", „−1", „0". */
  readonly amount: string;
  readonly tone: 'plus' | 'minus' | 'zero';
  /** „3 do rozl.", „2" — empty when the server sent no figure. */
  readonly after: string;
}

export interface LedgerRowsContext {
  readonly zone: string;
  /** The client's sessions in hand, by id — what names a deduction. */
  readonly sessions: ReadonlyMap<number, BookingSession>;
}

/** „pt 18.09" — the history's own date: the list's day, lower-cased as the table writes it. */
function ledgerDay(day: CivilDay, t: TFunction): string {
  const words = listDay(day, t);
  return `${words.charAt(0).toLowerCase()}${words.slice(1)}`;
}

/** „pt 18.09, 10:00" — a moment on the trainer's clock. */
function ledgerMoment(ms: number, zone: string, t: TFunction): string {
  return `${ledgerDay(wallReading(ms, zone).day, t)}, ${wallClock(ms, zone)}`;
}

function disputedKey(session: BookingSession | undefined): string | undefined {
  return session?.wireStatus == null ? undefined : DISPUTED[session.wireStatus];
}

/**
 * „Nieobecność · wt 22.09, 9:00" — the session a row is about. `withCause: false` when the row's
 * TITLE already says the cause, so the line names the session by its kind instead of saying it twice.
 */
function sessionWords(
  session: BookingSession,
  zone: string,
  t: TFunction,
  withCause = true,
): string {
  const disputed = withCause ? disputedKey(session) : undefined;
  const label = disputed === undefined ? sessionTitle(session, t) : t(disputed);
  const at = session.slotMs ?? session.startMs;
  return at === null || !session.hasHour
    ? `${label} · ${session.day === null ? '' : ledgerDay(session.day, t)}`
    : `${label} · ${ledgerMoment(at, zone, t)}`;
}

export function ledgerRows(
  movements: readonly LedgerMovement[],
  context: LedgerRowsContext,
  t: TFunction,
): LedgerRow[] {
  // When each charged session was charged, for the forgiveness naming it when the schedule in hand
  // does not hold that session.
  const chargedAt = new Map<number, number>();
  for (const movement of movements) {
    const at = Date.parse(movement.at);
    if (movement.kind === 'deduction' && movement.calendarEventId != null && !Number.isNaN(at)) {
      chargedAt.set(movement.calendarEventId, at);
    }
  }

  return movements.map((movement, index) => {
    const at = Date.parse(movement.at);
    const kind = movement.kind ?? '';
    const session =
      movement.calendarEventId == null ? undefined : context.sessions.get(movement.calendarEventId);
    const note = movement.note?.trim() ?? '';
    // A charge for a late cancel or a no-show is titled by that cause: the session did not take
    // place, so „Odbyta sesja" would be false. Without the session in hand the cause is unknown and
    // the row keeps the kind's own title, as mobile's.
    const cause = kind === 'deduction' ? disputedKey(session) : undefined;
    const parts: string[] = [];
    if (kind === 'deduction') {
      if (session !== undefined)
        parts.push(sessionWords(session, context.zone, t, cause === undefined));
      else if (!Number.isNaN(at)) parts.push(ledgerMoment(at, context.zone, t));
    }
    if (kind === 'forgiveness' && movement.calendarEventId != null) {
      const charged = chargedAt.get(movement.calendarEventId);
      const moment =
        session !== undefined
          ? sessionWords(session, context.zone, t)
          : charged === undefined
            ? null
            : ledgerMoment(charged, context.zone, t);
      if (moment !== null) parts.push(t(`${LEDGER}.forgivenFor`, { moment }));
    }
    if (note !== '') parts.push(note);
    const amount =
      movement.amount > 0
        ? `+${String(movement.amount)}`
        : movement.amount < 0
          ? `${MINUS}${String(Math.abs(movement.amount))}`
          : '0';
    return {
      key: `${movement.at}|${String(movement.calendarEventId ?? 'x')}|${String(index)}`,
      day: Number.isNaN(at) ? '' : ledgerDay(wallReading(at, context.zone).day, t),
      title:
        cause === undefined
          ? t(`${LEDGER}.kind.${KNOWN_KINDS.has(kind) ? kind : 'unknown'}`)
          : t(cause),
      meta: parts.length === 0 ? null : parts.join(' · '),
      amount,
      tone: movement.amount > 0 ? 'plus' : movement.amount < 0 ? 'minus' : 'zero',
      after:
        movement.balanceAfter == null
          ? ''
          : standingShort(standingOfBalance(movement.balanceAfter), t),
    };
  });
}

// ── The sessions a charge may still be cancelled for ─────────────────────────────────────────

/**
 * How far the tab reads the client's schedule: the forgiveness window around today
 * (`FORGIVENESS_WINDOW_DAYS`, the session panel's own rule), widened back to the oldest movement
 * in hand so every deduction on screen can be named by its session.
 */
export function ledgerSessionDays(
  today: CivilDay,
  oldest: CivilDay | null,
  windowDays: number,
): { readonly first: CivilDay; readonly last: CivilDay } {
  const back = shiftDay(today, -windowDays);
  return {
    first: oldest !== null && oldest < back ? oldest : back,
    last: shiftDay(today, windowDays),
  };
}

/**
 * The charges the trainer may still take back (T-C6): the session panel's own decision
 * (`chargeMayBeForgiven` — a late cancel or a no-show that cost a credit, within the window), less
 * those the ledger in hand already forgave, newest first. The ledger filter is best-effort — the
 * history is paged — and safe: naming the same session twice writes nothing new.
 */
export function forgivableSessions(
  sessions: readonly BookingSession[],
  movements: readonly LedgerMovement[],
  nowMs: number,
  zone: string,
): BookingSession[] {
  return sessions
    .filter(
      (session) =>
        chargeMayBeForgiven(session, nowMs, zone) && !isChargeForgiven(movements, session.id),
    )
    .sort(
      (a, b) =>
        (b.slotMs ?? b.startMs ?? Number.NEGATIVE_INFINITY) -
        (a.slotMs ?? a.startMs ?? Number.NEGATIVE_INFINITY),
    );
}

/** „wt 22.09, 9:00" over „Trening · nieobecność" — one forgivable session's two lines. */
export function forgivableWords(
  session: BookingSession,
  zone: string,
  t: TFunction,
): { readonly when: string; readonly what: string } {
  const at = session.slotMs ?? session.startMs;
  const status = session.wireStatus === null ? undefined : DISPUTED[session.wireStatus];
  return {
    when:
      at === null || !session.hasHour
        ? session.day === null
          ? ''
          : ledgerDay(session.day, t)
        : ledgerMoment(at, zone, t),
    what: t('web:booking.credits.act.forgivenessRow', {
      kind: sessionTitle(session, t),
      status: status === undefined ? '' : t(status).toLowerCase(),
    }),
  };
}
